import { Link } from 'react-router-dom';
import type { Comic } from '@/mocks/comics';

interface ComicCardProps {
  comic: Comic;
  variant?: 'default' | 'compact' | 'featured';
  rank?: number;
}

export default function ComicCard({ comic, variant = 'default', rank }: ComicCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/comic/${comic.slug}`}
        className="group flex gap-3 rounded-lg bg-background-100 p-3 transition-all duration-200 hover:bg-background-200"
      >
        <div className="w-16 h-[88px] flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
            title={`${comic.title} - 韩国漫画免费在线阅读`}
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <div>
            <h3 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
              {comic.title}
            </h3>
            <p className="text-xs text-foreground-600 mt-0.5">{comic.author}</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="flex items-center gap-0.5">
              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
              {comic.rating}
            </span>
            <span>{comic.status}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/comic/${comic.slug}`}
        className="group relative flex flex-col rounded-xl overflow-hidden bg-background-100 transition-all duration-300 hover:scale-[1.02]"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
            title={`${comic.title} - 韩漫下拉式在线阅读`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          {rank && (
            <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm font-bold">
              {rank}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="text-white font-heading text-lg truncate">{comic.title}</h3>
            <p className="text-white/70 text-xs mt-1">{comic.author}</p>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/comic/${comic.slug}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-100 transition-all duration-200 hover:scale-[1.03]"
      data-product-shop=""
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={comic.cover}
          alt={comic.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
          title={`${comic.title} - 韩国漫画大全免费`}
        />
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium text-white whitespace-nowrap ${
            comic.status === '连载中' ? 'bg-primary-500' : 'bg-secondary-500'
          }`}>
            {comic.status}
          </span>
        </div>
        {rank && (
          <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center text-white text-xs font-bold">
            {rank}
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium text-foreground-950 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-foreground-500">{comic.author}</span>
          <span className="flex items-center gap-0.5 text-xs text-accent-500">
            <i className="ri-star-fill text-[10px]"></i>
            {comic.rating}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {comic.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 rounded text-[10px] bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}